/**
 * SwapRegistrationModal
 * ---------------------
 * Intercambia un integrante ya inscrito (que ya no irá) por otro miembro del
 * mismo club. El que ENTRA hereda el precio y el estado de pago del que SALE
 * (el cupo pagado se cede tal cual; no se cobra diferencia).
 *
 * Disponible para director del club (su club) y admin de asociación.
 *
 * Tras el intercambio:
 *  - El consentimiento del que sale queda invalidado (lo hace el hook).
 *  - El consentimiento del que entra se pre-crea en estado pendiente (aquí),
 *    para que el director lo complete antes del evento. NUNCA se heredan
 *    documentos personales del que salió.
 */

import { useMemo, useState } from "react";
import { Modal, ModalFooter, Button, Input, Badge } from "@/components/ui";
import { ArrowRightLeft, Search, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "react-hot-toast";
import { useSwapRegistration } from "@/hooks/useEvents";
import { useMembersByClub } from "@/hooks/usePersons";
import { initConsent } from "@/lib/services/eventConsents";
import { formatCurrency } from "@/lib/utils";
import { useAuthStore } from "@/stores/authStore";
import type {
  Event,
  EventRegistration,
  MemberWithPerson,
  Member,
  Person,
} from "@/types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  event: Event;
  /** Inscripción del integrante que SALE (debe estar activa). */
  outgoingRegistration: EventRegistration;
  /** Nombre del club (para el consentimiento del que entra). */
  clubName: string;
  /** Nombre de la asociación (para el consentimiento del que entra). */
  associationName?: string;
}

const getMemberName = (m: MemberWithPerson): string => {
  const p = m.person;
  if (p?.fullName) return p.fullName;
  if (p?.firstName || p?.lastName)
    return `${p.firstName || ""} ${p.lastName || ""}`.trim();
  return m.personName || "Sin nombre";
};

export default function SwapRegistrationModal({
  isOpen,
  onClose,
  event,
  outgoingRegistration,
  clubName,
  associationName,
}: Props) {
  const reg = outgoingRegistration;
  const { user } = useAuthStore();
  const swap = useSwapRegistration();

  const { data: members, isLoading: loadingMembers } = useMembersByClub(
    reg.clubId,
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Candidatos: miembros del club, excluyendo al que sale. (No se filtra por
  // "ya inscritos" para no requerir cargar todas las inscripciones aquí; el
  // servicio bloquea con un error claro si el elegido ya está inscrito activo.)
  const candidates = useMemo(() => {
    if (!members) return [];
    const allowed =
      event.clubTypesAllowed && event.clubTypesAllowed.length > 0
        ? event.clubTypesAllowed
        : null;
    const search = searchTerm.trim().toLowerCase();
    return members.filter((m) => {
      if (m.personId && m.personId === reg.personId) return false;
      if (allowed) {
        const sec = m.staffSection ?? m.section;
        if (!sec || !allowed.includes(sec)) return false;
      }
      if (search) return getMemberName(m).toLowerCase().includes(search);
      return true;
    });
  }, [members, searchTerm, event.clubTypesAllowed, reg.personId]);

  const selected = useMemo(
    () => candidates.find((m) => m.id === selectedMemberId) ?? null,
    [candidates, selectedMemberId],
  );

  const handleClose = () => {
    if (submitting) return;
    setSearchTerm("");
    setSelectedMemberId(null);
    setReason("");
    onClose();
  };

  const handleConfirm = async () => {
    if (!selected || !selected.personId) {
      toast.error("Selecciona un integrante con perfil de persona válido");
      return;
    }
    setSubmitting(true);
    try {
      const incomingName = getMemberName(selected);
      const result = await swap.mutateAsync({
        outgoingRegistrationId: reg.id,
        eventId: reg.eventId,
        outgoingMemberId: reg.memberId,
        incoming: {
          personId: selected.personId,
          personName: incomingName,
          memberId: selected.id,
        },
        reason: reason.trim() || undefined,
      });

      // Pre-crear el consentimiento del que ENTRA en estado pendiente.
      // No bloquea el éxito del intercambio si falla.
      try {
        const { person, ...memberOnly } = selected;
        await initConsent({
          event,
          registration: {
            ...reg,
            id: result.newRegistrationId,
            personId: selected.personId,
            personName: incomingName,
            memberId: selected.id,
          },
          member: memberOnly as Member,
          person: person as Person,
          clubName,
          associationName: associationName ?? event.organizerName ?? "",
          userId: user?.uid,
        });
      } catch (e) {
        console.error("[SwapRegistrationModal] pre-crear consent falló", e);
        toast(
          "Intercambio hecho. Inicia el consentimiento del nuevo integrante manualmente.",
          { icon: "⚠️" },
        );
      }

      handleClose();
    } catch {
      // El hook ya muestra el toast de error.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Intercambiar integrante"
      description={`${reg.eventName} · ${reg.clubName}`}
      size="lg"
    >
      <div className="space-y-5 px-1">
        {/* SALE */}
        <section className="border rounded-xl p-4 bg-gray-50/60">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            Sale
          </h3>
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="font-medium text-gray-900">{reg.personName}</p>
              <p className="text-sm text-gray-500">
                {formatCurrency(reg.unitPrice)}
              </p>
            </div>
            <Badge
              className={
                reg.paymentStatus === "paid"
                  ? "bg-green-100 text-green-700"
                  : "bg-amber-100 text-amber-700"
              }
            >
              {reg.paymentStatus === "paid" ? "Pagado" : "Pendiente"}
            </Badge>
          </div>
        </section>

        {/* Flecha */}
        <div className="flex items-center justify-center text-purple-500">
          <ArrowRightLeft className="w-5 h-5" />
        </div>

        {/* ENTRA */}
        <section className="border rounded-xl p-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            Entra
          </h3>
          <div className="relative mb-3">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar miembro del club…"
              className="pl-9"
            />
          </div>

          {loadingMembers ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Cargando miembros…
            </div>
          ) : candidates.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">
              No hay miembros disponibles para este evento.
            </p>
          ) : (
            <div className="max-h-56 overflow-y-auto divide-y rounded-lg border">
              {candidates.map((m) => {
                const isSel = m.id === selectedMemberId;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setSelectedMemberId(m.id)}
                    className={`w-full text-left px-3 py-2.5 flex items-center justify-between gap-2 transition-colors ${
                      isSel ? "bg-purple-50" : "hover:bg-gray-50"
                    }`}
                  >
                    <span className="text-sm text-gray-800">
                      {getMemberName(m)}
                    </span>
                    {isSel && (
                      <span className="text-xs font-medium text-purple-600">
                        Seleccionado
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* Herencia de precio */}
        {selected && (
          <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2.5 text-sm text-blue-800">
            <strong>{getMemberName(selected)}</strong> heredará el precio de{" "}
            <strong>{reg.personName}</strong>: {formatCurrency(reg.unitPrice)} (
            {reg.paymentStatus === "paid" ? "pagado" : "pendiente"}).{" "}
            <span className="font-medium">No se cobra diferencia.</span>
          </div>
        )}

        {/* Aviso de consentimiento (menores) */}
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-sm text-amber-800 flex gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            El consentimiento del integrante que entra queda{" "}
            <strong>pendiente</strong> y debe completarse antes del evento. No se
            reutilizan los documentos del que sale.
          </span>
        </div>

        {/* Razón (auditoría) */}
        <Input
          label="Razón del cambio (opcional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Ej: el integrante no podrá asistir"
        />
      </div>

      <ModalFooter>
        <Button variant="secondary" onClick={handleClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={!selected || submitting}
          className="flex items-center gap-2"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Intercambiando…
            </>
          ) : (
            <>
              <ArrowRightLeft className="w-4 h-4" /> Confirmar intercambio
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
